import FancytreeNode = Fancytree.FancytreeNode;
import {BaseTree, BaseTreeBasicNode, BaseTreeNodeData} from "./baseTree";
import {breadcrumbsWithoutName, htmlEscape} from "../utils/string";
import {Identifiable} from "@/shared/timr";

export interface TreeDropdownConfig {
	rootElement: JQuery;
	emptyMessage?: string;
	emptyTreeMessage?: string;
	noMatchMessage?: string;
	clearEntryMessage?: string;
	clearable?: boolean;
	minQueryLength?: number;
	isNodeSelectable?: (data: unknown) => boolean;
	clickFolderMode?: 1 | 2 | 3 | 4;
	classDropdownElement?: string;
}

export interface TreeDropdownURLs {
	loadRootNodesURL: string;
	loadSubNodesURL: string;
	filterNodesURL: string;
}

export abstract class TreeDropdown<K extends Identifiable, C extends TreeDropdownConfig> {

	public static EVENT_SELECTED = "selected";
	public static EVENT_QUERY_CHANGED = "queryChanged";

	private tree: BaseTree;
	private lastQuery: string;
	private queryDelay = 350;
	private queryDelayTimeout: number;
	private needsQuery = false;
	protected selectedData: K | null | undefined;
	protected dropdownIsOpen = false;
	private isFirstFocus = true;

	private classCaretDown = "fa-caret-down";
	private classCaretUp = "fa-caret-up";
	private classClear = "fa-times";

	private classIdElement = "tree-dropdown-id";
	private classQueryElement = "tree-dropdown-query";
	private classTreeElement = "tree-dropdown-tree";
	private classCaretElement = "tree-dropdown-caret";
	private classDescriptionElement = "tree-dropdown-description";
	private classDropdownElement = "dropdown-menu";

	public static connect<T extends Identifiable, C extends TreeDropdownConfig>(dropdownA: TreeDropdown<T, C>, dropdownB: TreeDropdown<T, C>) {
		dropdownA.connectToOtherDropdown(dropdownB);
		dropdownB.connectToOtherDropdown(dropdownA);
	}

	constructor(public config: C, public urls: TreeDropdownURLs) {
		if (this.config.clearable === undefined) {
			this.config.clearable = true;
		}
		if (this.config.classDropdownElement !== undefined) {
			this.classDropdownElement = this.config.classDropdownElement;
		}
		this.initTree();
		this.initInputs();
		this.initCaret();
	}

	public getSelectedId(): number | null {
		const id = this.config.rootElement.find("." + this.classIdElement).val() as string;
		return id ? parseInt(id, 10) : null;
	}

	public setSelection(id?: number, description?: string, breadcrumbs?: string, data?: K) {
		this.config.rootElement.find("." + this.classIdElement).val(id);
		this.selectedData = data;
		this.updateDescription(description, breadcrumbs);
		this.updateCaret();
	}

	public reset() {
		this.queryNodes("");
	}

	public connectToOtherDropdown(otherDropdown: TreeDropdown<K, C>) {
		$(otherDropdown).on(TreeDropdown.EVENT_QUERY_CHANGED, (e, query: string) => {
			this.queryNodes(query, true);
		});
	}

	public queryIfNeeded() {
		if (this.needsQuery) {
			this.doQueryNodes();
		}
	}

	public getSelectedData(): K | null | undefined {
		return this.selectedData;
	}

	protected isNodeSelectable(data: K & BaseTreeBasicNode): boolean {
		return true;
	}

	protected abstract formatNode(data: K & BaseTreeBasicNode): string;

	protected queryNodes(query: string, waitUntilOpen = false) {
		this.lastQuery = query;
		if (waitUntilOpen && !this.dropdownIsOpen) {
			this.needsQuery = true;
		} else {
			clearTimeout(this.queryDelayTimeout);
			this.queryDelayTimeout = setTimeout(() => {
				this.doQueryNodes();
			}, this.queryDelay);
		}
	}

	protected doQueryNodes() {
		this.needsQuery = false;
		clearTimeout(this.queryDelayTimeout);

		this.tree.queryTree(this.lastQuery);

		$(this).triggerHandler(TreeDropdown.EVENT_QUERY_CHANGED, [this.lastQuery]);
	}

	protected getRequestData(): Record<string, unknown> {
		return {};
	}

	private initTree() {
		this.tree = new BaseTree({
			treeElement: this.config.rootElement.find("." + this.classTreeElement),
			loadRootNodesURL: this.urls.loadRootNodesURL,
			loadSubNodesURL: this.urls.loadSubNodesURL,
			filterNodesURL: this.urls.filterNodesURL,
			emptyTreeMessage: this.config.emptyTreeMessage,
			noMatchMessage: this.config.noMatchMessage,
			getRequestData: () => {
				return this.getRequestData();
			},
			isNodeSelectable: (data: unknown) => {
				// BaseTree exposes node data as unknown; this dropdown's nodes are K & BaseTreeBasicNode.
				const node = data as K & BaseTreeBasicNode;
				if (node.clearEntryNode) {
					return true;
				}
				return this.config.isNodeSelectable != null ? this.config.isNodeSelectable(node) : this.isNodeSelectable(node);
			},
			minQueryLength: this.config.minQueryLength,
			clickFolderMode: this.config.clickFolderMode,
		});
		$(this.tree).on(BaseTree.EVENT_SELECTED, (e, data, node: Fancytree.FancytreeNode) => {
			this.onNodeSelected(node);
		});

		if (this.config.clearEntryMessage) {
			$(this.tree).on(BaseTree.EVENT_TREE_INITIALIZED, () => {
				this.addClearEntryMessage();
			});
		}
	}

	protected initInputs() {
		const queryElement = this.config.rootElement.find("." + this.classQueryElement);
		this.config.rootElement.find(".tree-dropdown-input").click(() => {
			queryElement.focus();
		});
		const descriptionElement = this.config.rootElement.find(".tree-dropdown-description");
		queryElement.focus(() => {
			queryElement.val(this.lastQuery);
			if (this.isFirstFocus) {
				this.isFirstFocus = false;
				queryElement.select();
			}
			this.openDropdown();
			if (!this.lastQuery || this.lastQuery === "") {
				descriptionElement
					.css("opacity", 0.3)
					.css("z-index", 1050);
			} else {
				descriptionElement.hide();
			}
			this.updateCaret(true);
			const activeNode = this.tree.getActiveNode();
			if (activeNode != null) {
				activeNode.setActive(false);
			}
			this.queryIfNeeded();
		}).blur(e => {
			this.updateCaret(false);
			descriptionElement
				.css("opacity", "")
				.css("z-index", "")
				.show();
		}).keydown(e => {
			if (e.keyCode === 9) { // tab: close and let the browser move focus natively
				this.closeDropdown();
			} else if (e.keyCode === 13) { // hide on enter, don't submit the form
				this.closeDropdown();
				return false;
			} else if (e.keyCode === 27) {	// just hide on escape
				this.closeDropdown();
				queryElement.blur();
				return false;
			}
		}).keyup(e => {
			if (e.keyCode === 9 || e.keyCode === 13 || e.keyCode === 27) { // hide on tab or enter or escape
				e.stopPropagation();
				return false;
			}
		}).on("input", e => {
			if (($(e.target).val() as string) !== "") {
				descriptionElement.hide();
			} else {
				descriptionElement.show();
			}
			// query on "input" instead of "keyup" so pasting via context menu also triggers the query
			this.queryNodes(queryElement.val() as string);
		});

		// Create a click proxy that is private to this instance of datepicker, for unbinding
		const outsideClickProxy = (e: JQuery.TriggeredEvent) => { this.clickedOutside(e); };
		// Bind global mousedown for hiding
		$(document).on("mousedown", outsideClickProxy).on("touchend", outsideClickProxy);

		this.clearDescription();
	}

	private initCaret() {
		const caretElement = this.config.rootElement.find("." + this.classCaretElement);
		caretElement.click(() => {
			if (this.dropdownIsOpen) {
				this.closeDropdown();
			} else if (this.config.clearable && caretElement.children("i").hasClass(this.classClear)) {
				this.clearSelection();
			} else {
				this.openDropdown();
			}
		}).mouseenter(() => {
			this.dropdownIsOpen = this.config.rootElement.find("." + this.classDropdownElement).is(":visible");
		});
	}

	private updateCaret(open?: boolean) {
		const caretElement = this.config.rootElement.find("." + this.classCaretElement);
		if (caretElement.length > 0) {
			const caretIconElement = caretElement.children("i");
			if (open !== undefined ? open : this.dropdownIsOpen) {
				caretIconElement.toggleClass(this.classCaretDown, false);
				caretIconElement.toggleClass(this.classCaretUp, true);
				caretIconElement.toggleClass(this.classClear, false);
			} else {
				const clearable = this.config.clearable && this.getSelectedId() != null;
				caretIconElement.toggleClass(this.classCaretDown, !clearable);
				caretIconElement.toggleClass(this.classCaretUp, false);
				caretIconElement.toggleClass(this.classClear, clearable);
			}
		}
	}

	protected openDropdown() {
		const queryElement = this.config.rootElement.find("." + this.classQueryElement);
		if (queryElement.is(":focus")) {
			this.config.rootElement.find("." + this.classDropdownElement).show();
		} else {
			queryElement.focus();
		}
		this.dropdownIsOpen = true;
	}

	public closeDropdown() {
		this.config.rootElement.find("." + this.classDropdownElement).hide();
		this.dropdownIsOpen = false;
		this.isFirstFocus = true;
	}

	private clickedOutside(e: JQuery.TriggeredEvent) {
		const target = $(e.target);
		// if the page is clicked anywhere except within the input or dropdown itself then call hide it
		if (e.type !== "focusin" /* ie modal dialog fix */ &&
			target.closest(this.config.rootElement.find("." + this.classQueryElement)).length === 0 &&
			target.closest(this.config.rootElement.find("." + this.classDropdownElement)).length === 0 &&
			target.closest(this.config.rootElement.find("." + this.classCaretElement)).length === 0) {
			this.closeDropdown();
		}
	}

	private onNodeSelected(node: FancytreeNode) {
		this.updateDescription(this.getDescription(node), this.getBreadcrumbs(node));
		this.closeDropdown();
		const data = node.data as K & BaseTreeBasicNode;
		if (this.getSelectedId() !== data.id) {
			this.setSelectedId(data.id);
			this.selectedData = data;
			$(this).triggerHandler(TreeDropdown.EVENT_SELECTED, [data]);
		}
	}

	private clearSelection() {
		this.reset();
		this.clearDescription();
		this.closeDropdown();
		if (this.getSelectedId() != null) {
			this.setSelectedId(null);
			this.selectedData = null;
			$(this).triggerHandler(TreeDropdown.EVENT_SELECTED);
		}
	}

	protected setSelectedId(id?: number) {
		this.config.rootElement.find("." + this.classIdElement).val(id);
		this.updateCaret(false);
	}

	private getDescription(node: Fancytree.FancytreeNode): string {
		return this.formatNode(node.data as K & BaseTreeBasicNode);
	}

	private getBreadcrumbs(node: Fancytree.FancytreeNode): string {
		let breadcrumbs = this.formatNode(node.data as K & BaseTreeBasicNode);
		let currentNode = node.parent;
		while (!currentNode.isRootNode()) {
			breadcrumbs = this.formatNode(currentNode.data as K & BaseTreeBasicNode) + "/" + breadcrumbs;
			currentNode = currentNode.parent;
		}
		return breadcrumbs;
	}

	private clearDescription() {
		this.updateDescription(null, null);
	}

	protected updateDescription(description?: string, breadcrumbs?: string) {
		const descriptionElement = this.config.rootElement.find("." + this.classDescriptionElement);
		const breadcrumbsWithoutDescription = breadcrumbsWithoutName(breadcrumbs || "", description || "");
		if (!description && this.config.emptyMessage) {
			descriptionElement.removeAttr("title");
			descriptionElement.text(this.config.emptyMessage);
		} else if (!breadcrumbsWithoutDescription) {
			descriptionElement.attr("title", breadcrumbs || "");
			descriptionElement.text(description || "");
		} else {
			descriptionElement.attr("title", breadcrumbs || "");
			descriptionElement.html(`
					<div>${htmlEscape(description || "")}</div>
					<div>${breadcrumbsWithoutDescription}</div>
				`);
		}
	}

	/**
	 * Adds the clearEntryMessage to the treeDropdown.
	 * The clearEntryMessage is added to clear the selected item when this item is clicked in the tree containing object
	 *
	 * @private
	 */
	private addClearEntryMessage() {
		const countChildren = this.tree.getRoot().countChildren();

		// if the list contains only the empty node, the clearEntryMessage is not added
		if (countChildren === 1 && (this.tree.getRoot().children[0].data as BaseTreeNodeData).emptyNode) {
			return;
		}
		if (!this.lastQuery || this.lastQuery.length === 0 || countChildren > 1 || countChildren === 1 && !(this.tree.getRoot().children[0].data as BaseTreeNodeData).dummy) {
			const clearEntryNode: BaseTreeBasicNode = {
				dummy: false,
				emptyNode: true,
				title: this.config.clearEntryMessage,
				children: [],
				unselectable: false,
				clearEntryNode: true
			};
			this.tree.getRoot().addNode(clearEntryNode, "firstChild");
		}
	}
}
